/**
 * Kubernetes Pod-Level Cost Attribution (Patent Sub-system M)
 * ============================================================
 * Game-theoretic Shapley-value attribution for shared K8s overhead.
 * Nobody else does this — every other tool splits by proportional share.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Boxes, Calculator, Loader2, FlaskConical, Upload, Sparkles,
  TrendingDown, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import ProvenByTests from '../components/ProvenByTests';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const TIER_COLOR = {
  'prod-api': 'bg-blue-400',
  'prod-ml': 'bg-fuchsia-400',
  'prod-growth': 'bg-amber-400',
};

const K8sCost = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/k8s-cost/demo-attribute`),
      axios.get(`${API_URL}/k8s-cost/signals`),
    ]).then(([res, sig]) => {
      setResult(res.data);
      setSignals(sig.data.signals || []);
    }).catch(() => toast.error('Could not load cluster data'))
      .finally(() => setLoading(false));
  }, []);

  const handleCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/k8s-cost/upload-csv`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Parsed ${r.data.parsed_pods} pods from your CSV`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV parse failed');
    } finally {
      setUploading(false);
    }
  };

  const top10 = result ? [...result.pods].sort((a, b) => b.total_monthly_usd - a.total_monthly_usd).slice(0, 10) : [];
  const maxCost = top10.length ? top10[0].total_monthly_usd : 1;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="k8s-cost-page">
      <Helmet>
        <title>K8s Pod-Level Cost Attribution via Shapley Value | CARTIEAI</title>
        <meta name="description" content="The first FinOps tool that uses Shapley value for fair attribution of shared Kubernetes cluster overhead. Patent-pending sub-system M." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-blue-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="k8s-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
            K8s Shapley Attribution
          </h1>
          <div className="mt-3"><ProvenByTests engine="shapley" /></div>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            Kubecost, Vantage, and Cloudability split shared cluster overhead by <span className="text-white font-semibold">proportional resource share</span> — oversimplified, gameable, and unfair to small workloads. We use <span className="text-blue-300">game-theoretic Shapley value</span> — provably fair.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            For every pod: direct cost (CPU × memory × GPU × egress × PV) plus its <strong className="text-blue-300">Shapley share of cluster shared overhead</strong> (control plane + monitoring + load balancer + idle node capacity) — computed via Monte-Carlo sampled Shapley (Castro et al. 2009) over the coalition game.
          </p>
        </div>

        {/* SUMMARY */}
        {result && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cluster monthly</div>
                <div className="text-2xl font-bold text-white mt-1" data-testid="k8s-cluster-total">{fmtUsd(result.cluster_total_monthly_usd)}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Direct cost</div>
                <div className="text-2xl font-bold text-emerald-300 mt-1">{fmtUsd(result.direct_total_monthly_usd)}</div>
                <div className="text-[10px] text-slate-500 mt-1">CPU + mem + GPU + egress + PV</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Shared overhead (Shapley)</div>
                <div className="text-2xl font-bold text-blue-300 mt-1">{fmtUsd(result.shared_overhead_monthly_usd)}</div>
                <div className="text-[10px] text-slate-500 mt-1">{result.shapley_samples_used} samples</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pods analysed</div>
                <div className="text-2xl font-bold text-white mt-1">{result.pod_count}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-16 text-slate-500" />}

        {/* TEAM ROLLUP */}
        {result && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-slate-900/60 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Layers className="w-5 h-5 text-blue-300" /> Team rollup
                </CardTitle>
                <CardDescription>Auto-derived from namespace → team mapping.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="k8s-team-rollup">
                  {result.team_rollup.map((row) => {
                    const pct = (row.monthly_usd / result.cluster_total_monthly_usd) * 100;
                    return (
                      <div key={row.team}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white font-mono">{row.team}</span>
                          <span className="text-sm text-white font-mono font-semibold">{fmtUsd(row.monthly_usd)} · {pct.toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-slate-950 rounded overflow-hidden">
                          <div className="h-full bg-blue-400 bg-gradient-to-r from-blue-400 to-cyan-400" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-blue-300" /> Namespace rollup
                </CardTitle>
                <CardDescription>Per-namespace monthly spend.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="k8s-namespace-rollup">
                  {result.namespace_rollup.map((row) => {
                    const pct = (row.monthly_usd / result.cluster_total_monthly_usd) * 100;
                    return (
                      <div key={row.namespace} className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${TIER_COLOR[row.namespace] || 'bg-slate-500'}`} />
                        <span className="text-sm text-white font-mono flex-1">{row.namespace}</span>
                        <span className="text-sm text-slate-400 font-mono">{pct.toFixed(0)}%</span>
                        <span className="text-sm text-white font-mono font-semibold w-20 text-right">{fmtUsd(row.monthly_usd)}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TOP-10 PODS */}
        {result && (
          <Card className="mt-8 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Boxes className="w-5 h-5 text-blue-300" /> Top 10 pods by attributed cost
              </CardTitle>
              <CardDescription>Direct + Shapley share of shared overhead.</CardDescription>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm" data-testid="k8s-pod-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="text-left p-2 font-semibold">Pod</th>
                    <th className="text-left p-2 font-semibold">Namespace</th>
                    <th className="text-right p-2 font-semibold">CPU util</th>
                    <th className="text-right p-2 font-semibold">Mem util</th>
                    <th className="text-right p-2 font-semibold">Direct</th>
                    <th className="text-right p-2 font-semibold">Shapley</th>
                    <th className="text-right p-2 font-semibold">Total</th>
                    <th className="text-right p-2 font-semibold">Waste</th>
                  </tr>
                </thead>
                <tbody>
                  {top10.map((p) => (
                    <tr key={p.pod_id} className="border-b border-white/5 hover:bg-slate-950/60">
                      <td className="p-2 text-xs text-white font-mono truncate max-w-[280px]">{p.pod_id}</td>
                      <td className="p-2 text-xs">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${TIER_COLOR[p.namespace] || 'bg-slate-500'} mr-1.5`} />
                        <span className="text-slate-300 font-mono">{p.namespace}</span>
                      </td>
                      <td className="p-2 text-xs text-right font-mono text-slate-300">{p.cpu_util_pct.toFixed(0)}%</td>
                      <td className="p-2 text-xs text-right font-mono text-slate-300">{p.mem_util_pct.toFixed(0)}%</td>
                      <td className="p-2 text-xs text-right font-mono text-emerald-300">{fmtUsd(p.direct_subtotal_usd)}</td>
                      <td className="p-2 text-xs text-right font-mono text-blue-300">{fmtUsd(p.shapley_shared_usd)}</td>
                      <td className="p-2 text-xs text-right font-mono text-white font-semibold">{fmtUsd(p.total_monthly_usd)}</td>
                      <td className="p-2 text-xs text-right">
                        <span className={`font-mono font-semibold ${p.waste_pct > 50 ? 'text-red-300' : p.waste_pct > 30 ? 'text-amber-300' : 'text-slate-500'}`}>
                          {p.waste_pct.toFixed(0)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* CSV UPLOAD ESCAPE HATCH */}
        <Card className="mt-10 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border-emerald-500/30" data-testid="k8s-upload-card">
          <CardContent className="p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-blue-300 font-semibold mb-2">Try with YOUR cluster</div>
            <h3 className="text-2xl font-bold text-white">Upload `kubectl top pods` CSV</h3>
            <p className="text-slate-400 mt-2 max-w-2xl">No agent install. No kubeconfig. Just paste your pod list and we&apos;ll attribute every byte of shared overhead via Shapley value in 2 seconds.</p>
            <p className="text-xs text-slate-500 mt-2 font-mono">Required columns: <code className="text-blue-300">pod_id, namespace, cpu_req_cores, mem_req_gb</code>. Optional: <code className="text-blue-300">gpu_count, egress_gb_per_day, pv_gb</code>.</p>
            <div className="mt-4">
              <label htmlFor="csv-upload" className="cursor-pointer inline-flex items-center gap-2 bg-emerald-500 hover:bg-blue-400 text-slate-950 font-semibold px-4 py-2 rounded transition" data-testid="k8s-upload-label">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Parsing…' : 'Upload your pods CSV'}
              </label>
              <input id="csv-upload" type="file" accept=".csv,.txt" onChange={handleCsv} className="hidden" data-testid="k8s-upload-input" />
            </div>
          </CardContent>
        </Card>

        {/* SIGNAL CATALOG */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-blue-300 uppercase mb-2">Cost dimensions</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">{signals.length} signals fed into the algorithm. K1–K4 + K6 are direct cost; K5 is the inventive step — shared overhead attributed via Shapley.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="k8s-signal-catalog">
            {signals.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-white/10 rounded-md p-4">
                <div className="text-[10px] font-mono uppercase text-slate-500 mb-1">{s.id}</div>
                <div className="text-white font-semibold text-sm">{s.name}</div>
                <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default K8sCost;
